import { CAYMAN_ACADEMY_LOGO_BASE64 } from '../../assets/images/caymanAcademyLogoBase64';

// A faithful port of the staff web app's fee receipt, so a parent's printout and
// one printed from Fee Management > Payment are the same document. Source of
// truth, kept line-for-line:
//   markup  - src/frontend/.../studentFees/useFeeReceiptPrinter.ts
//   styles  - src/frontend/.../studentFees/FeeReceiptStyles.ts
//   wrapper - src/frontend/.../ui/Print/usePrint.ts
// If the staff receipt changes, change it here too.

export interface Letterhead {
  addressLine1: string;
  addressLine2: string;
  email: string;
  website: string;
  phone: string;
}

export const DEFAULT_LETTERHEAD: Letterhead = {
  addressLine1: '211 Walkers Road, George Town',
  addressLine2: 'Cayman Islands, KY1-1106',
  email: 'Office.secretary@caymanacademy.ky',
  website: 'www.caymanacademy.ky',
  phone: '1(345) 640-2630',
};

export interface ReceiptOptions {
  /** Prefix printed before every amount, as the staff receipt prints the organization's currency. */
  currency?: string;
  letterhead?: Letterhead;
}

const feeReceiptStyles = `
    body {  padding: 40px; color: #1f2937; max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; }
    .logo-section { display: flex; align-items: center; gap: 14px; }
    .logo-section .org-logo { height: 48px; max-width: 140px; object-fit: contain; }
    .logo-section h1 { margin: 0; font-size: 24px; color: #111827; letter-spacing: -0.5px; }
    .logo-section p { margin: 5px 0 0; color: #6b7280; font-size: 14px; }
    .receipt-title { text-align: right; }
    .receipt-title h2 { margin: 0; font-size: 32px; color: #d97706; text-transform: uppercase; letter-spacing: 1px; }
    .receipt-title p { margin: 5px 0 0; color: #6b7280; font-size: 14px; }

    /* Student details: label and value side by side, rows aligned across the two columns.
       It used to push each value to the far edge of its half (space-between), which left a
       wide empty gap after every label (QA: "excessive empty space"). */
    .info-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 32px; margin-bottom: 22px; background: #f9fafb; padding: 14px 18px; border-radius: 8px; }
    .info-row { display: flex; align-items: baseline; gap: 10px; min-width: 0; }
    .info-label { flex: 0 0 104px; font-weight: 600; color: #4b5563; font-size: 14px; }
    .info-value { flex: 1; min-width: 0; font-weight: 500; color: #111827; font-size: 14px; overflow-wrap: anywhere; }
    @media (max-width: 480px) {
      .info-grid { grid-template-columns: 1fr; gap: 8px; padding: 12px 14px; }
      .info-label { flex-basis: 96px; }
    }

    table { width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 13px; }
    th { background-color: #f3f4f6; color: #374151; font-weight: 600; text-align: left; padding: 12px 10px; border-bottom: 1px solid #e5e7eb; }
    td { padding: 12px 10px; border-bottom: 1px solid #f3f4f6; color: #4b5563; }
    .text-right { text-align: right; }
    .font-mono { font-family: monospace; }

    .totals-section { display: flex; justify-content: flex-end; }
    .totals-table { width: 40%; border-collapse: collapse; }
    .totals-table td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .totals-table .total-row td { border-top: 2px solid #374151; border-bottom: none; font-weight: 700; font-size: 16px; color: #111827; padding-top: 15px; }

    .footer { margin-top: 60px; text-align: center; color: #9ca3af; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }

    @media print {
        @page { margin: 0; size: auto; }
        body { -webkit-print-color-adjust: exact; padding: 20px; }
    }
`;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// dd/MM/yyyy, the way the staff receipt prints every date.
const formatDate = (value?: string | Date) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export const generateReceiptHTML = (
  installment: any,
  studentInfo: { name: string },
  options: ReceiptOptions = {},
) => {
  const currency = options.currency ?? '$';
  const org = options.letterhead ?? DEFAULT_LETTERHEAD;
  const money = (n?: number) => `${currency}${(n || 0).toFixed(2)}`;

  const grandTotal = installment.amount || 0;
  const discountTotal = installment.discount || 0;
  const paidAmount = installment.paidAmount || 0;
  const dueAmount = installment.balanceAmount || 0;
  const studentName = escapeHtml(studentInfo.name || 'N/A');
  const title = `Fee Receipt - ${studentInfo.name || 'Student'}`;

  const content = `
        <div class="header" style="border-bottom: 2px solid #196565; align-items: center; padding-bottom: 15px;">
          <div class="logo-section" style="gap: 15px;">
            <img src="${CAYMAN_ACADEMY_LOGO_BASE64}" alt="Logo" class="org-logo" style="height: 56px; max-width: none;" />
            <div style="display: flex; flex-direction: column;">
              <h1 style="margin: 0; font-size: 22px; font-weight: 700; color: #EEC906; line-height: 0.95; text-transform: uppercase; display: flex; flex-direction: column; letter-spacing: 0;">
                <span>CAYMAN</span>
                <span>ACADEMY</span>
              </h1>
              <div style="width: 100%; height: 2px; background-color: #196565; margin: 3px 0 2px 0;"></div>
              <p style="margin: 0; font-size: 7.5px; font-weight: 600; color: #196565; text-transform: uppercase; letter-spacing: 0.2px;">
                A SEVENTH-DAY ADVENTIST INSTITUTION
              </p>
            </div>
          </div>

          <div class="org-address-section" style="font-size: 11px; line-height: 1.5; color: #4b5563; text-align: left; padding-left: 20px;">
             <div style="color: #0000EE; text-decoration: underline;">${escapeHtml(org.addressLine1)}</div>
             <div style="color: #0000EE; text-decoration: underline;">${escapeHtml(org.addressLine2)}</div>
             <div>${escapeHtml(org.email)}</div>
             <div>${escapeHtml(org.website)}</div>
             <div>${escapeHtml(org.phone)}</div>
          </div>

          <div class="receipt-title">
            <h2 style="color: #196565; margin: 0; font-size: 28px; text-transform: uppercase;">Receipt</h2>
            <p style="margin: 5px 0 0; color: #6b7280; font-size: 14px;">Fee Invoice Statement</p>
          </div>
        </div>

        <div class="info-grid">
          <div class="info-row"><span class="info-label">Student Name:</span> <span class="info-value">${studentName}</span></div>
          <div class="info-row"><span class="info-label">Date:</span> <span class="info-value">${formatDate(new Date())}</span></div>
          <div class="info-row"><span class="info-label">Invoice No:</span> <span class="info-value">${escapeHtml(installment.invoiceNumber || 'N/A')}</span></div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Due Date</th>
              <th>Status</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Disc.</th>
              <th class="text-right">Paid</th>
              <th class="text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
              <tr>
                <td>
                  <strong>${escapeHtml(installment.type || '-')}</strong><br/>
                  <span style="font-size: 11px; color: #6b7280;">${escapeHtml(installment.installmentName || '-')}</span>
                </td>
                <td>${formatDate(installment.dueDate)}</td>
                <td>${installment.status === 'paid' ? 'Paid' : 'Unpaid'}</td>
                <td class="text-right font-mono">${money(grandTotal)}</td>
                <td class="text-right font-mono">${money(discountTotal)}</td>
                <td class="text-right font-mono">${money(paidAmount)}</td>
                <td class="text-right font-mono">${money(dueAmount)}</td>
              </tr>
          </tbody>
        </table>
        <div class="totals-section">
          <table class="totals-table">
              <tr>
                  <td>Subtotal Amount</td>
                  <td class="text-right font-mono">${money(grandTotal + discountTotal)}</td>
              </tr>
              <tr>
                  <td>Discount</td>
                  <td class="text-right font-mono">-${money(discountTotal)}</td>
              </tr>
              <tr>
                  <td>Total Paid</td>
                  <td class="text-right font-mono">${money(paidAmount)}</td>
              </tr>
              <tr class="total-row">
                  <td>Total Due</td>
                  <td class="text-right font-mono">${money(dueAmount)}</td>
              </tr>
          </table>
        </div>
    `;

  return `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${escapeHtml(title)}</title>
            <style>
              @media print {
                @page { margin: 20px; }
                body {
                  font-family: Arial, sans-serif;
                  font-size: 12px;
                  color: #333;
                  -webkit-print-color-adjust: exact;
                }
              }
              ${feeReceiptStyles}
            </style>
          </head>
          <body>
            ${content}
          </body>
        </html>
      `;
};
